import React, { useEffect, useState } from 'react';
import axios from 'axios';
import AdminHeader from '../components/Layout/AdminHeader';
import AdminSideBar from '../components/Admin/Layout/AdminSideBar';
import { DataGrid } from '@mui/x-data-grid';
import { Button, CircularProgress } from '@mui/material';
import { server } from '../server';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const AdminDashboardSales = () => {
  const [rows, setRows] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${server}/invoice/export-invoices`, {
        params: startDate && endDate ? { startDate, endDate } : {},
        withCredentials: true,
      });
  
      // Sort the invoices so that the most recent comes first.
      const sortedInvoices = response.data.sort(
        (a, b) => new Date(b.date) - new Date(a.date)
      );
  
      setRows(
        sortedInvoices.map((invoice) => ({
          id: invoice._id,
          invoiceNumber: invoice.invoiceNumber,
          invoiceDate: invoice.date.slice(0, 10),
          buyerName: invoice.buyerDetails.name,
          buyerGstNumber: invoice.buyerDetails.gstNumber,
          totalValueWithoutGst: invoice.totalValueWithoutGst.toFixed(2),
          totalGstValue: invoice.totalGstValue.toFixed(2),
          shippingCharges: invoice.shippingCharges.toFixed(2),
          totalInvoiceValue: invoice.totalInvoiceValue.toFixed(2),
          status: invoice.status,
        }))
      );
    } catch (error) {
      console.error('Failed to fetch invoices', error);
      alert('Failed to fetch invoices. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadInvoice = async (invoiceId) => {
    try {
      const response = await fetch(`${server}/invoice/download-invoice/${invoiceId}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          Accept: 'application/pdf',
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = `invoice_${invoiceId}.pdf`;
        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?(.+)"?/);
          if (match && match[1]) {
            filename = match[1];
          }
        }
        saveAs(blob, filename);
      } else {
        const errorText = await response.text();
        console.error('Failed to download invoice:', errorText);
        alert('Failed to download invoice. Please try again.');
      }
    } catch (error) {
      console.error('Error fetching invoice:', error);
      alert('An error occurred while downloading the invoice.');
    }
  };

  const handleEditInvoice = (invoiceId) => {
    navigate(`/admin-edit-invoice/${invoiceId}`);
  };

  const handleCancelInvoice = async (invoiceId) => {
    if (window.confirm('Are you sure you want to cancel this invoice?')) {
      try {
        const response = await axios.delete(`${server}/invoice/cancel-invoice/${invoiceId}`, {
          withCredentials: true,
        });
        if (response.data.success) {
          alert('Invoice canceled successfully');
          // Refresh the invoices list
          setRows((prevRows) => prevRows.filter((row) => row.id !== invoiceId));
        } else {
          alert('Failed to cancel invoice');
        }
      } catch (error) {
        console.error('Failed to cancel invoice', error);
        alert('An error occurred while canceling the invoice.');
      }
    }
  };

  const handleDownloadExcel = async () => {
    if (rows.length === 0) {
      alert('No data available to download.');
      return;
    }

    setLoading(true);
    try {
      const dataToExport = rows.map((row) => ({
        'Invoice Number': row.invoiceNumber,
        'Invoice Date': row.invoiceDate,
        'Buyer Name': row.buyerName,
        'Buyer GST Number': row.buyerGstNumber,
        'Total Value Without GST': parseFloat(row.totalValueWithoutGst),
        'Total GST Value': parseFloat(row.totalGstValue),
        'Shipping Charges': parseFloat(row.shippingCharges),
        'Total Invoice Value': parseFloat(row.totalInvoiceValue),
        Status: row.status,
      }));

      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Invoices');
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
      const fileName = `Invoices_${startDate || 'Any'}_to_${endDate || 'Any'}.xlsx`;
      saveAs(blob, fileName);
    } catch (error) {
      console.error('Error generating Excel file:', error);
      alert('An error occurred while generating the Excel file.');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { field: 'invoiceNumber', headerName: 'Invoice Number', width: 150 },
    { field: 'invoiceDate', headerName: 'Invoice Date', width: 150 },
    { field: 'buyerName', headerName: 'Buyer Name', width: 150 },
    { field: 'buyerGstNumber', headerName: 'Buyer GST Number', width: 200 },
    { field: 'totalValueWithoutGst', headerName: 'Total Value (Without GST)', width: 200 },
    { field: 'totalGstValue', headerName: 'Total GST Value', width: 150 },
    { field: 'shippingCharges', headerName: 'Shipping Charges', width: 150 },
    { field: 'totalInvoiceValue', headerName: 'Total Invoice Value', width: 180 },
    {
      field: 'downloadInvoice',
      headerName: 'Download Invoice',
      width: 180,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          onClick={() => handleDownloadInvoice(params.row.id)}
          variant="contained"
          color="primary"
          style={{ fontSize: "0.8rem" }}
        >
          Download PDF
        </Button>
      ),
    },
    {
      field: 'editInvoice',
      headerName: 'Edit Invoice',
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          onClick={() => handleEditInvoice(params.row.id)}
          variant="contained"
          color="default"
          style={{ marginLeft: 8, fontSize: "0.8rem" }}
        >
          Edit
        </Button>
      ),
    },
    {
      field: 'cancelInvoice',
      headerName: 'Cancel Invoice',
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          onClick={() => handleCancelInvoice(params.row.id)}
          variant="contained"
          color="secondary"
          style={{ fontSize: "0.8rem" }}
        >
          Cancel
        </Button>
      ),
    },
    { field: 'status', headerName: 'Status', minWidth: 120 },
  ];

  return (
    <div className="dashboard-page-wrapper">
      <AdminHeader />
      <div className="dashboard-flex-wrapper">
        <div className="w-[80px] 800px:w-[330px] dashboard-sidebar">
          <AdminSideBar active={5} />
        </div>
        <div className="dashboard-main-content flex-1">
              <div className="flex justify-between mb-4">
                <div className="flex items-center">
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="p-2 border rounded mr-2"
                    placeholder="Start Date"
                  />
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="p-2 border rounded"
                    placeholder="End Date"
                  />
                </div>
                <div className="flex space-x-2">
                  <Button
                    onClick={handleDownloadExcel}
                    variant="contained"
                    color="primary"
                    disabled={loading}
                  >
                    {loading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      'Download Excel'
                    )}
                  </Button>
                </div>
              </div>

              <style>
                {`
                  @media print {
                    body * {
                      visibility: hidden;
                    }
                    .printable-area, .printable-area * {
                      visibility: visible;
                    }
                    .printable-area {
                      position: absolute;
                      left: 0;
                      top: 0;
                      width: 100%;
                    }
                  }
                `}
              </style>

              <div className="printable-area">
                {loading ? (
                  <div className="flex justify-center items-center h-full">
                    <CircularProgress />
                  </div>
                ) : (
                  <div className="datagrid-scroll-wrapper">
                    <div className="table-inner-container" style={{ minWidth: "1930px" }}>
                      <DataGrid
                        rows={rows}
                        columns={columns}
                        pageSize={20}
                        disableSelectionOnClick
                        autoHeight
                        sx={{
                          fontSize: "0.8rem",
                          border: "none",
                          "& .MuiDataGrid-cell": { fontSize: "0.8rem", padding: "0.5rem" },
                          "& .MuiDataGrid-columnHeader": { fontSize: "0.8rem", padding: "0.5rem" },
                          "& .MuiDataGrid-virtualScroller": {
                            overflow: "visible !important",
                          },
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardSales;
